/** @shell standard @header bar */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { View, Text, useWindowDimensions } from 'react-native';
import Animated from 'react-native-reanimated';
import { AppLinearGradient as LinearGradient } from '../components/ui/AppLinearGradient';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { SvgIcon } from '../components/common/SvgIcon';
import { MainLoader } from '../components/load-main/MainLoader';
import { ScreenHeader, useScreenHeaderScroll, HeaderIconButton } from '../components/common/ScreenHeader';
import { SCROLL_VIEW_PROPS } from '../shared/utils/scrollListPerf';
import { useRoute } from '@react-navigation/native';
import { appTheme, hexToRgba, useThemeStore } from '../shared/theme';
import type { ThemeColors } from '../shared/theme/store';
import { useAuthStore } from '../shared/auth/store';
import { useAuthNavigation, useExitOrGoBack } from '../shared/navigation/useAuthNavigation';
import { fetchSubscriptionPlans, shouldOfferFreeDaysModal, applyV3CatalogPlanDayOffFields, setSelectedSubscription, resolvePlanOfferPricing, type PlanItem } from '../data/domains/subscription';
import { computeJourneyUserNeeds, rankPlanItemsForJourney } from '../data/domains/journey';
import { isJourneyByApiEnabled } from '../data/domains/config';
import { useTranslation } from '../shared/i18n';
import { AnimatedSection } from '../components/common/AnimatedSection';
import { StackScreenShell } from '../shared/theme/studio/stackScreenShell';
import { Clickable } from '../components/common/Clickable';
import { useAuthSheets } from '../shared/auth/authSheetContext';
import { BottomSheetPopup } from '../components/common/BottomSheetPopup';
import { HealthInformationPopupScrollBody } from '../healthInformation/components/HealthInformationScrollBody';
import { useHealthInformationCms } from '../healthInformation/components/HealthInformationCmsContext';
import {
  button,
  buttonFilled,
  buttonOutlined,
  buttonText,
  buttonTextOutline,
  buttonWrap,
  summaryScreenRoot,
  summaryLoaderViewport,
  createSummaryLoaderCaptionStyle,
  summaryScroll,
  summaryHighlightBand,
  summaryHighlightBandContent,
  summaryHighlightEyebrow,
  summaryHighlightMetric,
  summaryHighlightUnit,
  summaryStatPillRow,
  createSummaryStatPillSurfaceStyle,
  createSummaryStatValueStyle,
  createSummaryStatCaptionStyle,
  createSummaryStatusDotStyle,
  summaryMetricChipRow,
  createSummaryMetricChipSurfaceStyle,
  createSummaryMetricChipValueStyle,
  createSummaryMetricChipCaptionStyle,
  summaryCatalogSection,
  createSummarySectionHeadingStyle,
  createSummaryEmptyStateCardStyle,
  createSummaryEmptyStateIconWellStyle,
  createSummaryEmptyStateTitleStyle,
  createSummaryEmptyStateSubtitleStyle,
  summarySolidCtaPadding,
  createSheetStackingBodyStyle,
  sheetStackingPrimaryActionPadding,
  sheetStackingPrimaryActionLabel,
  createSheetStackingSecondaryActionSurfaceStyle,
  createSheetStackingSecondaryActionLabelStyle,
  createSheetRadioPanelMessageStyle,
  createSheetRadioPanelAccentHighlightStyle,
  sheetRadioOptionList,
  createSheetRadioRowSurfaceStyle,
  createSheetRadioRowSelectedOverlayStyle,
  createSheetRadioOuterDiscStyle,
  createSheetRadioInnerDiscStyle,
  createSheetRadioRowCaptionStyle,
  sheetRadioConfirmActionPadding,
  sheetRadioConfirmActionLabel,
  androidAppHeaderContentTop,
  androidScaleSize,
} from '../shared/theme/sharedStyles';
import { SubscriptionCatalogPlanCard } from '../subscriptions/components/SubscriptionCatalogPlanCard';
import { createSubscriptionListStyles } from '../subscriptions/components/subscriptionListStyles';
import { resolveWebLayoutWidth } from '../shared/platform/webLayout';

const JOURNEY_RESULT_LOADER_SIZE = androidScaleSize(84);

function getBmiCategory(bmiValue: number, colors: ThemeColors): { labelKey: string; color: string } {
  if (bmiValue < 18.5) {
    return { labelKey: 'underweight', color: colors.warning };
  }
  if (bmiValue < 25) {
    return { labelKey: 'normal', color: colors.success };
  }
  if (bmiValue < 30) {
    return { labelKey: 'overweight', color: colors.warning };
  }
  return { labelKey: 'obese', color: colors.error };
}

function buildPlanItems(categories: any[]): PlanItem[] {
  const items: PlanItem[] = [];
  for (const category of categories ?? []) {
    const subs = category.subscriptions ?? [];
    for (const sub of subs) {
      const dayVal = Number(sub.days ?? sub.type_key ?? sub.duration ?? 0);
      if (dayVal <= 0) {
        continue;
      }
      const typeData = sub.type_data ?? [];
      for (const plan of typeData) {
        if (plan.is_active === false) {
          continue;
        }
        items.push({
          key: `plan-${category.id}-${dayVal}-${plan.subscription_id ?? plan.id}`,
          plan: applyV3CatalogPlanDayOffFields(
            {
              ...plan,
              days: dayVal,
              type_key: dayVal,
              duration: dayVal,
              category_id: category.id,
              subscription_category_id: category.id,
            },
            category,
          ),
          days: dayVal,
          categoryTitle: category.title ?? '',
          categoryImage: category.attachment ?? category.thumbnail,
        });
      }
    }
  }
  /** Highest display price first (active offer, else list cost / price). */
  items.sort(
    (a, b) => resolvePlanOfferPricing(b.plan).displayPrice - resolvePlanOfferPricing(a.plan).displayPrice,
  );
  return items;
}

const typeLabelKeyMap: Record<string, string> = { loss: 'weightLoss', gain: 'weightGain', between: 'maintain' };

export const JourneyResultScreen: React.FC = () => {
  const { t } = useTranslation();
  const navigation = useAuthNavigation();
  const { openLogin, openRegister } = useAuthSheets();
  const route = useRoute();
  const insets = useSafeAreaInsets();
  const colors = useThemeStore(s => s.colors);
  const appearance = useThemeStore(s => s.appearance);
  const user = useAuthStore(s => s.user);
  const on_back_exit = useExitOrGoBack();
  const { scrollY, onScroll } = useScreenHeaderScroll();
  const raw_params = (route.params as any) || {};
  const params = raw_params && typeof raw_params === 'object' ? raw_params : {};
  const calories = params.calories != null ? Number(params.calories) : undefined;
  const type = params.type != null ? String(params.type) : undefined;
  const weight = params.weight != null ? Number(params.weight) : undefined;
  const height = params.height != null ? Number(params.height) : undefined;
  const age = params.age != null ? Number(params.age) : undefined;
  const gender = params.gender != null ? String(params.gender) : undefined;
  const activity_id =
    params.activity_id != null
      ? String(params.activity_id)
      : params.activity != null
        ? String(params.activity)
        : params.activity_level != null
          ? String(params.activity_level)
          : undefined;
  const type_label = t(`journeyResult.${typeLabelKeyMap[String(type || '')] || 'maintain'}`);

  const [isLoading, setIsLoading] = useState(true);
  const [planItems, setPlanItems] = useState<PlanItem[]>([]);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [showFreeDaysModal, setShowFreeDaysModal] = useState(false);
  const [selectedPlanWithFreeDays, setSelectedPlanWithFreeDays] = useState<any>(null);
  const [includeFreeDays, setIncludeFreeDays] = useState(false);
  const [pendingCheckoutPlan, setPendingCheckoutPlan] = useState<any>(null);
  const [health_info_sheet_open, setHealthInfoSheetOpen] = useState(false);
  const { isAvailable: health_info_available, pageTitle: health_info_page_title } = useHealthInformationCms();

  const has_params = calories != null && type != null && Number(calories) > 0;
  const heightM = height && height > 0 ? height / 100 : 0;
  const bmi = weight && heightM > 0 ? weight / (heightM * heightM) : 0;
  const safeColors = colors ?? appTheme.colors;
  const bmiCategory = getBmiCategory(bmi, safeColors);

  const { width: window_width } = useWindowDimensions();
  const styles = useMemo(
    () => createSubscriptionListStyles(safeColors, resolveWebLayoutWidth(window_width)),
    [safeColors, window_width],
  );
  const loader_text_style = useMemo(() => createSummaryLoaderCaptionStyle(safeColors), [safeColors]);
  const stat_pill_style = useMemo(() => createSummaryStatPillSurfaceStyle(safeColors), [safeColors]);
  const stat_label_style = useMemo(() => createSummaryStatCaptionStyle(safeColors), [safeColors]);
  const macro_pill_style = useMemo(() => createSummaryMetricChipSurfaceStyle(safeColors), [safeColors]);
  const macro_label_style = useMemo(() => createSummaryMetricChipCaptionStyle(safeColors), [safeColors]);
  const section_title_style = useMemo(() => createSummarySectionHeadingStyle(safeColors), [safeColors]);
  const empty_card_style = useMemo(() => createSummaryEmptyStateCardStyle(safeColors), [safeColors]);
  const empty_icon_wrap = useMemo(() => createSummaryEmptyStateIconWellStyle(safeColors), [safeColors]);
  const empty_title_style = useMemo(() => createSummaryEmptyStateTitleStyle(safeColors), [safeColors]);
  const empty_subtitle_style = useMemo(() => createSummaryEmptyStateSubtitleStyle(safeColors), [safeColors]);
  const modal_message_style = useMemo(() => createSheetStackingBodyStyle(safeColors), [safeColors]);
  const modal_secondary_btn = useMemo(() => createSheetStackingSecondaryActionSurfaceStyle(safeColors), [safeColors]);
  const modal_secondary_text = useMemo(() => createSheetStackingSecondaryActionLabelStyle(safeColors), [safeColors]);
  const free_days_message = useMemo(() => createSheetRadioPanelMessageStyle(safeColors), [safeColors]);
  const free_days_highlight = useMemo(() => createSheetRadioPanelAccentHighlightStyle(safeColors), [safeColors]);
  const free_opt_base = useMemo(() => createSheetRadioRowSurfaceStyle(safeColors), [safeColors]);
  const free_opt_selected = useMemo(() => createSheetRadioRowSelectedOverlayStyle(safeColors), [safeColors]);

  const fetchPlans = useCallback(async () => {
    setIsLoading(true);
    try {
      const journey_by_api = isJourneyByApiEnabled();
      if (!journey_by_api) {
        // Local mode: always fetch full catalog, then reorder via `localSuggest.ts` (never drop plans).
        const categories = await fetchSubscriptionPlans();
        let items = buildPlanItems(categories);
        if (
          type &&
          weight != null &&
          height != null &&
          age != null &&
          gender &&
          activity_id
        ) {
          const needs = computeJourneyUserNeeds({
            goal: type as 'loss' | 'gain' | 'between',
            gender,
            age,
            height_cm: height,
            weight_kg: weight,
            activity: activity_id,
          });
          items = rankPlanItemsForJourney(items, needs);
        } else {
          // Still show the full list — price-desc fallback if needs inputs are incomplete.
          items.sort(
            (a, b) => resolvePlanOfferPricing(b.plan).displayPrice - resolvePlanOfferPricing(a.plan).displayPrice,
          );
        }
        setPlanItems(items);
        return;
      }

      let categories = await fetchSubscriptionPlans({ calories: Number(calories), type: String(type) });
      let items = buildPlanItems(categories);
      if (items.length === 0) {
        categories = await fetchSubscriptionPlans();
        items = buildPlanItems(categories);
      }
      setPlanItems(items);
    } catch (_err) {
      setPlanItems([]);
    } finally {
      setIsLoading(false);
    }
  }, [calories, type, weight, height, age, gender, activity_id]);

  const fetchedRef = useRef(false);
  useEffect(() => {
    if (!has_params) {
      setIsLoading(false);
      return;
    }
    if (fetchedRef.current) {
      return;
    }
    fetchedRef.current = true;
    fetchPlans();
  }, [has_params, fetchPlans]);

  const handleSelectPlan = useCallback(
    async (item: PlanItem) => {
      const plan = item.plan;
      const show_free_days_modal = shouldOfferFreeDaysModal(plan);
      const plan_base = applyV3CatalogPlanDayOffFields(plan as Record<string, unknown>);
      const planForCheckout = {
        ...plan_base,
        days: item.days,
        id: plan.id ?? plan.subscription_id,
        subscription_id: plan.subscription_id ?? plan.id,
        celebrity_id: plan.celebrity_id ?? 0,
        number_of_meals: plan.number_of_meals ?? plan.total_meals ?? 0,
        number_of_snacks: plan.number_of_snacks ?? plan.total_snacks ?? 0,
        customized_meals: plan.customized_meals ?? plan.is_customized ?? false,
        isCustomAllowed: plan.isCustomAllowed ?? plan.is_customized ?? false,
        free_days_selected: 0,
      };
      if (show_free_days_modal) {
        setSelectedPlanWithFreeDays(planForCheckout);
        setIncludeFreeDays(false);
        setShowFreeDaysModal(true);
        return;
      }
      if (!user) {
        setPendingCheckoutPlan(planForCheckout);
        await setSelectedSubscription(planForCheckout);
        setShowSignupModal(true);
        return;
      }
      navigation.navigate('Checkout', { plan: planForCheckout, free_days_selected: 0 });
    },
    [navigation, user],
  );

  const handleConfirmFreeDays = useCallback(async () => {
    if (!selectedPlanWithFreeDays) {
      return;
    }
    const free_add = includeFreeDays ? Number(selectedPlanWithFreeDays.free_days) : 0;
    const planWithFreeDays = {
      ...selectedPlanWithFreeDays,
      free_days_selected: free_add,
    };
    setShowFreeDaysModal(false);
    setSelectedPlanWithFreeDays(null);
    if (!user) {
      setPendingCheckoutPlan(planWithFreeDays);
      await setSelectedSubscription(planWithFreeDays);
      setShowSignupModal(true);
      return;
    }
    navigation.navigate('Checkout', { plan: planWithFreeDays, free_days_selected: free_add });
  }, [selectedPlanWithFreeDays, includeFreeDays, navigation, user]);

  const cal = Number(calories) || 2000;
  const proteinGrams = Math.round((cal * 0.3) / 4);
  const carbsGrams = Math.round((cal * 0.4) / 4);
  const fatGrams = Math.round((cal * 0.3) / 9);
  const headerHeight = androidAppHeaderContentTop(insets.top);
  const contentPaddingBottom = insets.bottom + 48;

  const cal_stat_value = useMemo(() => createSummaryStatValueStyle({ color: safeColors.primary }), [safeColors.primary]);
  const bmi_stat_value = useMemo(() => createSummaryStatValueStyle({ color: bmiCategory.color }), [bmiCategory.color]);
  const bmi_dot = useMemo(() => createSummaryStatusDotStyle({ color: bmiCategory.color }), [bmiCategory.color]);
  const protein_val = useMemo(() => createSummaryMetricChipValueStyle({ color: safeColors.protein }), [safeColors.protein]);
  const carbs_val = useMemo(() => createSummaryMetricChipValueStyle({ color: safeColors.carbs }), [safeColors.carbs]);
  const fat_val = useMemo(() => createSummaryMetricChipValueStyle({ color: safeColors.fats }), [safeColors.fats]);

  const is_dark = appearance === 'dark';
  const health_info_glass_surface = useMemo(
    () => ({
      iconColor: hexToRgba(safeColors.danger, is_dark ? 0.93 : 0.86),
    }),
    [safeColors.danger, is_dark],
  );

  const health_info_header_right = useMemo(
    () => (
      <HeaderIconButton
        onPress={() => setHealthInfoSheetOpen(true)}
        accessibilityLabel={t('healthInformation.openInfoAccessibility')}
        appearance="outline"
        haptics>
        <SvgIcon name="health-alert" size={22} color={health_info_glass_surface.iconColor} />
      </HeaderIconButton>
    ),
    [health_info_glass_surface.iconColor, t],
  );

  if (!has_params) {
    return (
      <StackScreenShell colors={safeColors}>
        <ScreenHeader title={t('journeyResult.yourResults')} onBack={on_back_exit} rightAction={health_info_available ? health_info_header_right : undefined} scrollY={scrollY} />
        <View style={[summaryLoaderViewport, { paddingTop: headerHeight + androidScaleSize(48) }]}>
          <Text style={loader_text_style}>{t('journeyResult.missingJourneyData')}</Text>
          <Clickable
            onPress={on_back_exit}
            showBlur
            blurBorderRadius={appTheme.borderRadius.xl}
            blurType="light"
            blurAmount={5}
            style={{ alignSelf: 'center', marginTop: appTheme.spacing.lg }}
            accessibilityLabel={t('journeyResult.startOver')}
            accessibilityRole="button"
            haptics>
            <View style={[buttonWrap, { shadowColor: safeColors.shadowColor }]}>
              <View style={[button, buttonFilled, { backgroundColor: safeColors.primary }, summarySolidCtaPadding]}>
                <Text style={[buttonText, { color: safeColors.onPrimary }]}>{t('journeyResult.startOver')}</Text>
              </View>
            </View>
          </Clickable>
        </View>
      </StackScreenShell>
    );
  }

  return (
    <StackScreenShell colors={safeColors}>
      <ScreenHeader title={t('journeyResult.yourResults')} onBack={on_back_exit} rightAction={health_info_available ? health_info_header_right : undefined} scrollY={scrollY} />

      {isLoading ? (
        <View style={[summaryLoaderViewport, { paddingTop: headerHeight + androidScaleSize(48) }]}>
          <MainLoader
            size={JOURNEY_RESULT_LOADER_SIZE}
            color={safeColors.primary}
            accessibilityLabel={t('journeyResult.findingPlans')}
          />
          <Text style={loader_text_style}>{t('journeyResult.findingPlans')}</Text>
        </View>
      ) : (
        <Animated.ScrollView
          {...SCROLL_VIEW_PROPS}
          style={summaryScroll}
          onScroll={onScroll}
          scrollEventThrottle={16}
          contentContainerStyle={{ paddingTop: headerHeight + appTheme.spacing.md, paddingBottom: contentPaddingBottom, paddingHorizontal: appTheme.spacing.lg }}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled">
          <LinearGradient
            colors={[safeColors.primary, safeColors.primaryDark ?? safeColors.primary]}
            style={[summaryHighlightBand, { shadowColor: safeColors.shadowColor }]}
            start={{ x: 0.15, y: 0 }}
            end={{ x: 0.85, y: 1 }}>
            <AnimatedSection index={0} use_fade_only>
              <View style={summaryHighlightBandContent}>
                <Text style={summaryHighlightEyebrow}>
                  {type_label} {t('journeyResult.goal')}
                </Text>
                <Text style={[summaryHighlightMetric, { color: safeColors.onPrimary }]}>{Number(cal).toFixed(0)}</Text>
                <Text style={summaryHighlightUnit}>{t('journeyResult.kcalPerDay')}</Text>
              </View>
            </AnimatedSection>
          </LinearGradient>

          <View style={summaryStatPillRow}>
            <AnimatedSection index={1}>
              <View style={stat_pill_style}>
                <SvgIcon name="menu" size={20} color={safeColors.primary} />
                <Text style={cal_stat_value}>{Number(cal).toFixed(0)}</Text>
                <Text style={stat_label_style}>{t('foodItemDetail.cal')}</Text>
              </View>
            </AnimatedSection>
            <AnimatedSection index={2}>
              <View style={stat_pill_style}>
                <View style={bmi_dot} />
                <Text style={bmi_stat_value}>{Number(bmi).toFixed(1)}</Text>
                <Text style={stat_label_style}>{t(`journeyResult.${bmiCategory.labelKey}`)}</Text>
              </View>
            </AnimatedSection>
          </View>

          <AnimatedSection index={3}>
            <View style={summaryMetricChipRow}>
              <View style={macro_pill_style}>
                <Text style={protein_val}>{proteinGrams}g</Text>
                <Text style={macro_label_style}>{t('journeyResult.protein')}</Text>
              </View>
              <View style={macro_pill_style}>
                <Text style={carbs_val}>{carbsGrams}g</Text>
                <Text style={macro_label_style}>{t('journeyResult.carbs')}</Text>
              </View>
              <View style={macro_pill_style}>
                <Text style={fat_val}>{fatGrams}g</Text>
                <Text style={macro_label_style}>{t('journeyResult.fat')}</Text>
              </View>
            </View>
          </AnimatedSection>

          {planItems.length > 0 ? (
            <View style={summaryCatalogSection}>
              <AnimatedSection index={4}>
                <Text style={section_title_style}>{t('journeyResult.recommendedPlans')}</Text>
              </AnimatedSection>
              {planItems.map((item, i) => (
                <AnimatedSection
                  key={item.key}
                  index={i}
                  style={i < planItems.length - 1 ? { marginBottom: appTheme.spacing.lg } : undefined}>
                  <SubscriptionCatalogPlanCard
                    item={item}
                    colors={safeColors}
                    styles={styles}
                    onSelect={() => handleSelectPlan(item)}
                  />
                </AnimatedSection>
              ))}
            </View>
          ) : (
            <AnimatedSection index={0} use_fade_only>
              <View style={empty_card_style}>
                <View style={empty_icon_wrap}>
                  <SvgIcon name="menu" size={44} color={safeColors.primary} />
                </View>
                <Text style={empty_title_style}>{t('journeyResult.noPlansAvailable')}</Text>
                <Text style={empty_subtitle_style}>{t('journeyResult.browsePlansSubtitle')}</Text>
                <Clickable
                  onPress={() => navigation.navigate('SubscriptionList')}
                  showBlur
                  blurBorderRadius={appTheme.borderRadius.xl}
                  blurType="light"
                  blurAmount={5}
                  style={{ alignSelf: 'center' }}
                  accessibilityLabel={t('journeyResult.browsePlans')}
                  accessibilityRole="button"
                  haptics>
                  <View style={[buttonWrap, { shadowColor: safeColors.shadowColor }]}>
                    <View style={[button, buttonFilled, { backgroundColor: safeColors.primary }, summarySolidCtaPadding]}>
                      <Text style={[buttonText, { color: safeColors.onPrimary }]}>{t('journeyResult.browsePlans')}</Text>
                    </View>
                  </View>
                </Clickable>
              </View>
            </AnimatedSection>
          )}
        </Animated.ScrollView>
      )}

      <BottomSheetPopup visible={showSignupModal} onClose={() => setShowSignupModal(false)} title={t('journeyResult.signUpToContinue')} animationType="slide" showDragHandle>
        <View style={{ paddingHorizontal: appTheme.spacing.lg, paddingBottom: appTheme.spacing.lg }}>
          <Text style={modal_message_style}>{t('journeyResult.createAccountOrLogin')}</Text>
          <Clickable
            onPress={async () => {
              setShowSignupModal(false);
              if (pendingCheckoutPlan) {
                await setSelectedSubscription(pendingCheckoutPlan);
              }
              openRegister({ subData: pendingCheckoutPlan });
            }}
            showBlur
            blurBorderRadius={appTheme.borderRadius.xl}
            blurType="light"
            blurAmount={5}
            style={{ alignSelf: 'stretch', marginTop: appTheme.spacing.md }}
            accessibilityLabel={t('journeyResult.signUp')}
            accessibilityRole="button"
            haptics>
            <View style={[buttonWrap, { shadowColor: safeColors.shadowColor }]}>
              <View style={[button, buttonFilled, { backgroundColor: safeColors.primary }, sheetStackingPrimaryActionPadding]}>
                <Text style={[sheetStackingPrimaryActionLabel, { color: safeColors.onPrimary }]}>{t('journeyResult.signUp')}</Text>
              </View>
            </View>
          </Clickable>
          <Clickable
            onPress={async () => {
              setShowSignupModal(false);
              if (pendingCheckoutPlan) {
                await setSelectedSubscription(pendingCheckoutPlan);
              }
              openLogin();
            }}
            showBlur
            blurBorderRadius={appTheme.borderRadius.xl}
            blurType="light"
            blurAmount={5}
            style={{ alignSelf: 'stretch', marginTop: appTheme.spacing.sm }}
            accessibilityLabel={t('journeyResult.logIn')}
            accessibilityRole="button"
            haptics>
            <View style={[buttonWrap, { shadowColor: safeColors.shadowColor }]}>
              <View style={[button, buttonOutlined, modal_secondary_btn]}>
                <Text style={[buttonTextOutline, modal_secondary_text]}>{t('journeyResult.logIn')}</Text>
              </View>
            </View>
          </Clickable>
        </View>
      </BottomSheetPopup>

      <BottomSheetPopup
        visible={showFreeDaysModal}
        onClose={() => {
          setShowFreeDaysModal(false);
          setSelectedPlanWithFreeDays(null);
        }}
        title={t('subscriptions.freeDays')}
        animationType="slide"
        showDragHandle>
        <View style={{ paddingHorizontal: appTheme.spacing.lg, paddingBottom: appTheme.spacing.lg }}>
          <Text style={free_days_message}>
            {t('subscriptions.addFreeDaysQuestion')}
            <Text style={free_days_highlight}>{selectedPlanWithFreeDays?.free_days}</Text>
            {t('subscriptions.freeDaysSuffix')}
          </Text>
          <View style={sheetRadioOptionList}>
            <Clickable
              style={[free_opt_base, !includeFreeDays ? free_opt_selected : null]}
              onPress={() => setIncludeFreeDays(false)}
              accessibilityLabel={t('subscriptions.noThanks')}
              accessibilityRole="button"
              haptics>
              <View style={createSheetRadioOuterDiscStyle(safeColors, { selected: !includeFreeDays })}>{!includeFreeDays ? <View style={createSheetRadioInnerDiscStyle(safeColors)} /> : null}</View>
              <Text style={createSheetRadioRowCaptionStyle(safeColors, { active: !includeFreeDays })}>{t('subscriptions.noThanks')}</Text>
            </Clickable>
            <Clickable
              style={[free_opt_base, includeFreeDays ? free_opt_selected : null]}
              onPress={() => setIncludeFreeDays(true)}
              accessibilityLabel={t('subscriptions.yesFreeDays')}
              accessibilityRole="button"
              haptics>
              <View style={createSheetRadioOuterDiscStyle(safeColors, { selected: includeFreeDays })}>{includeFreeDays ? <View style={createSheetRadioInnerDiscStyle(safeColors)} /> : null}</View>
              <Text style={createSheetRadioRowCaptionStyle(safeColors, { active: includeFreeDays })}>
                {t('subscriptions.yesFreeDays')}
                {selectedPlanWithFreeDays?.free_days}
                {t('subscriptions.yesFreeDaysSuffix')}
              </Text>
            </Clickable>
          </View>
          <Clickable
            onPress={handleConfirmFreeDays}
            showBlur
            blurBorderRadius={appTheme.borderRadius.xl}
            blurType="light"
            blurAmount={5}
            style={{ alignSelf: 'stretch', marginTop: appTheme.spacing.md }}
            accessibilityLabel={t('subscriptions.continue')}
            accessibilityRole="button"
            haptics>
            <View style={[buttonWrap, { shadowColor: safeColors.shadowColor }]}>
              <View style={[button, buttonFilled, { backgroundColor: safeColors.primary }, sheetRadioConfirmActionPadding]}>
                <Text style={[sheetRadioConfirmActionLabel, { color: safeColors.onPrimary }]}>{t('subscriptions.continue')}</Text>
              </View>
            </View>
          </Clickable>
        </View>
      </BottomSheetPopup>

      {health_info_available ? (
      <BottomSheetPopup
        visible={health_info_sheet_open}
        onClose={() => setHealthInfoSheetOpen(false)}
        title={health_info_page_title || t('healthInformation.title')}
        showDragHandle
        animationType="slide">
        <HealthInformationPopupScrollBody />
      </BottomSheetPopup>
      ) : null}
    </StackScreenShell>
  );
};
